import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  IdbMultiDeviceStore,
  revokeDevice,
  multiDeviceEvents,
} from "./MultiDeviceSessions";
import type { DeviceRecord } from "./MultiDeviceSessions";

// ---------------------------------------------------------------------------
// Test helpers
// ---------------------------------------------------------------------------

let _dbSeq = 0;
/** Each call returns a unique DB name so tests never share state. */
function freshDbName(): string {
  return `kaether:multi-device:selftest-${++_dbSeq}`;
}

function makeRecord(
  userId: string,
  deviceId: number,
  overrides: Partial<DeviceRecord> = {},
): DeviceRecord {
  return {
    userId,
    deviceId,
    identityPubKey: btoa(`fake-identity-key-${userId}-${deviceId}`),
    registrationId: 10000 + deviceId,
    label: `Test device ${deviceId}`,
    addedAt: new Date(2025, 0, 1).toISOString(),
    lastSeenAt: new Date(2025, 0, 1).toISOString(),
    status: "active",
    ...overrides,
  };
}

async function deleteDb(name: string): Promise<void> {
  await new Promise<void>((resolve) => {
    const req = indexedDB.deleteDatabase(name);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve(); // best-effort
    req.onblocked = () => resolve();
  });
}

// ---------------------------------------------------------------------------
// Individual test cases
// ---------------------------------------------------------------------------

/** TC-1: Add 3 devices and list; result is sorted by deviceId. */
async function tcAddAndList(): Promise<TestCase> {
  const name = "add + list — 3 devices returned, sorted by deviceId";
  const dbName = freshDbName();
  const store = new IdbMultiDeviceStore(dbName);
  try {
    const uid = "user-list-test";
    await store.add(uid, makeRecord(uid, 3));
    await store.add(uid, makeRecord(uid, 1));
    await store.add(uid, makeRecord(uid, 2));

    const records = await store.list(uid);
    if (records.length !== 3)
      return fail(name, `expected 3 records, got ${records.length}`);
    if (records[0].deviceId !== 1 || records[1].deviceId !== 2 || records[2].deviceId !== 3)
      return fail(
        name,
        `wrong order: ${records.map((r) => r.deviceId).join(",")}`,
      );
    return pass(name);
  } finally {
    store.close();
    await deleteDb(dbName);
  }
}

/** TC-2: Records survive a close + reopen of the DB connection. */
async function tcPersistence(): Promise<TestCase> {
  const name = "persistence — records survive DB close + reopen";
  const dbName = freshDbName();
  try {
    const uid = "user-persist";
    {
      const store = new IdbMultiDeviceStore(dbName);
      await store.add(uid, makeRecord(uid, 1));
      await store.add(uid, makeRecord(uid, 2));
      store.close();
    }
    // Open a fresh store instance (simulates a new page load).
    const store2 = new IdbMultiDeviceStore(dbName);
    try {
      const records = await store2.list(uid);
      if (records.length !== 2)
        return fail(name, `after reopen got ${records.length} records, want 2`);
      return pass(name);
    } finally {
      store2.close();
    }
  } finally {
    await deleteDb(dbName);
  }
}

/** TC-3: Records for userId A are not visible when listing userId B. */
async function tcPerUserIdIsolation(): Promise<TestCase> {
  const name = "per-userId isolation — list(B) returns [] when only A has devices";
  const dbName = freshDbName();
  const store = new IdbMultiDeviceStore(dbName);
  try {
    const uidA = "user-A";
    const uidB = "user-B";
    await store.add(uidA, makeRecord(uidA, 1));
    const records = await store.list(uidB);
    if (records.length !== 0)
      return fail(name, `expected [], got ${records.length} record(s)`);
    return pass(name);
  } finally {
    store.close();
    await deleteDb(dbName);
  }
}

/** TC-4: markRevoked flips status; record still present in list(). */
async function tcRevoke(): Promise<TestCase> {
  const name = "revoke — status flipped to 'revoked', record still in list";
  const dbName = freshDbName();
  const store = new IdbMultiDeviceStore(dbName);
  try {
    const uid = "user-revoke";
    await store.add(uid, makeRecord(uid, 1));
    await store.markRevoked(uid, 1);

    const records = await store.list(uid);
    if (records.length !== 1)
      return fail(name, `expected 1 record after revoke, got ${records.length}`);
    if (records[0].status !== "revoked")
      return fail(name, `expected status 'revoked', got '${records[0].status}'`);
    return pass(name);
  } finally {
    store.close();
    await deleteDb(dbName);
  }
}

/** TC-5: Re-add after revoke — status returns to active (latest-wins). */
async function tcReAddAfterRevoke(): Promise<TestCase> {
  const name = "re-add after revoke — status returns to 'active' (latest-wins upsert)";
  const dbName = freshDbName();
  const store = new IdbMultiDeviceStore(dbName);
  try {
    const uid = "user-re-add";
    await store.add(uid, makeRecord(uid, 1));
    await store.markRevoked(uid, 1);
    // Re-register the same deviceId with a fresh record (status: active).
    await store.add(uid, makeRecord(uid, 1, { status: "active" }));

    const records = await store.list(uid);
    if (records.length !== 1)
      return fail(
        name,
        `expected exactly 1 record after re-add (upsert), got ${records.length}`,
      );
    if (records[0].status !== "active")
      return fail(
        name,
        `expected status 'active' after re-add, got '${records[0].status}'`,
      );
    return pass(name);
  } finally {
    store.close();
    await deleteDb(dbName);
  }
}

/** TC-6: prune removes only records older than the threshold. */
async function tcPrune(): Promise<TestCase> {
  const name = "prune — removes stale records, keeps recent ones";
  const dbName = freshDbName();
  const store = new IdbMultiDeviceStore(dbName);
  try {
    const uid = "user-prune";
    const ninetyOneDaysAgo = new Date(Date.now() - 91 * 24 * 60 * 60 * 1000);
    const today = new Date();

    // Device 1: stale (last seen 91 days ago)
    await store.add(
      uid,
      makeRecord(uid, 1, {
        lastSeenAt: ninetyOneDaysAgo.toISOString(),
        addedAt: ninetyOneDaysAgo.toISOString(),
      }),
    );
    // Device 2: recent (last seen today)
    await store.add(
      uid,
      makeRecord(uid, 2, {
        lastSeenAt: today.toISOString(),
        addedAt: today.toISOString(),
      }),
    );

    const ninetyDaysMs = 90 * 24 * 60 * 60 * 1000;
    await store.prune(uid, ninetyDaysMs);

    const records = await store.list(uid);
    if (records.length !== 1)
      return fail(
        name,
        `expected 1 record after prune, got ${records.length}`,
      );
    if (records[0].deviceId !== 2)
      return fail(
        name,
        `expected device 2 to survive, got deviceId ${records[0].deviceId}`,
      );
    return pass(name);
  } finally {
    store.close();
    await deleteDb(dbName);
  }
}

/** TC-7: Two simultaneous adds of different deviceIds both succeed. */
async function tcConcurrentAdd(): Promise<TestCase> {
  const name = "concurrent add — two simultaneous adds of different deviceIds both succeed";
  const dbName = freshDbName();
  const store = new IdbMultiDeviceStore(dbName);
  try {
    const uid = "user-concurrent";
    // Fire both without awaiting between them.
    await Promise.all([
      store.add(uid, makeRecord(uid, 10)),
      store.add(uid, makeRecord(uid, 20)),
    ]);

    const records = await store.list(uid);
    if (records.length !== 2)
      return fail(
        name,
        `expected 2 records, got ${records.length}`,
      );
    const ids = records.map((r) => r.deviceId).sort((a, b) => a - b);
    if (ids[0] !== 10 || ids[1] !== 20)
      return fail(name, `unexpected deviceIds: ${ids.join(",")}`);
    return pass(name);
  } finally {
    store.close();
    await deleteDb(dbName);
  }
}

/** TC-8: Duplicate (userId, deviceId) upserts rather than creating two rows. */
async function tcDuplicateInvariant(): Promise<TestCase> {
  const name =
    "duplicate (userId, deviceId) — upsert: second add replaces first, exactly 1 row";
  const dbName = freshDbName();
  const store = new IdbMultiDeviceStore(dbName);
  try {
    const uid = "user-dup";
    const rec1 = makeRecord(uid, 1, { label: "first" });
    const rec2 = makeRecord(uid, 1, { label: "second" });

    await store.add(uid, rec1);
    await store.add(uid, rec2);

    const records = await store.list(uid);
    if (records.length !== 1)
      return fail(
        name,
        `expected exactly 1 row after two adds of same deviceId, got ${records.length}`,
      );
    if (records[0].label !== "second")
      return fail(
        name,
        `expected label 'second' (latest wins), got '${records[0].label}'`,
      );
    return pass(name);
  } finally {
    store.close();
    await deleteDb(dbName);
  }
}

/** TC-9: revokeDevice emits a device-revoked event on the module-level bus. */
async function tcRevokeEmitsEvent(): Promise<TestCase> {
  const name = "revokeDevice — emits device-revoked event on multiDeviceEvents bus";
  const dbName = freshDbName();
  const store = new IdbMultiDeviceStore(dbName);
  try {
    const uid = "user-event";
    await store.add(uid, makeRecord(uid, 5));

    let fired = false;
    let capturedDeviceId: number | null = null;
    const off = multiDeviceEvents.on((evt) => {
      if (evt.type === "device-revoked" && evt.userId === uid) {
        fired = true;
        capturedDeviceId = evt.deviceId;
      }
    });

    await revokeDevice(store, uid, 5);
    off();

    if (!fired)
      return fail(name, "device-revoked event was never emitted");
    if (capturedDeviceId !== 5)
      return fail(
        name,
        `event had deviceId ${capturedDeviceId}, expected 5`,
      );
    return pass(name);
  } finally {
    store.close();
    await deleteDb(dbName);
  }
}

/** TC-10: markRevoked on a non-existent deviceId is a silent no-op (no error). */
async function tcMarkRevokedNoOp(): Promise<TestCase> {
  const name = "markRevoked on unknown deviceId — silent no-op, no throw";
  const dbName = freshDbName();
  const store = new IdbMultiDeviceStore(dbName);
  try {
    const uid = "user-noop";
    // No devices registered — should not throw.
    await store.markRevoked(uid, 99);
    const records = await store.list(uid);
    if (records.length !== 0)
      return fail(name, `expected empty list, got ${records.length}`);
    return pass(name);
  } catch (err) {
    return fail(name, `threw unexpectedly: ${String(err)}`);
  } finally {
    store.close();
    await deleteDb(dbName);
  }
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Multi-device session listing",
  isVerified: true,
  run: async () =>
    buildSuiteResult(
      await Promise.all([
        tcAddAndList(),
        tcPersistence(),
        tcPerUserIdIsolation(),
        tcRevoke(),
        tcReAddAfterRevoke(),
        tcPrune(),
        tcConcurrentAdd(),
        tcDuplicateInvariant(),
        tcRevokeEmitsEvent(),
        tcMarkRevokedNoOp(),
      ]),
    ),
});
